import React, { useEffect, useState } from 'react';
import { useAccountSalespersons } from '../core/useAccountSalespersons';
import SearchableSelect from '../../../shared/components/SearchableSelect'


const SalespersonField = ({ value, onChange, className }) => {
  const { salespersons, loading, loaded, error, loadSalespersons, addSalesperson } = useAccountSalespersons();
  const [showModal, setShowModal] = useState(false);
  const [draftName, setDraftName] = useState('');
  const hasCurrentValue = !!value && !salespersons.includes(value);

  useEffect(() => {
    if (!loaded && !loading) {
      void loadSalespersons();
    }
  }, [loaded, loading, loadSalespersons]);

  const showEmpty = loaded && !loading && !error && salespersons.length === 0;
  const status = loading
    ? 'Loading salespersons...'
    : error
      ? 'Failed to load salespersons'
      : showEmpty
        ? 'No salespersons found'
        : '';

  const openModal = () => {
    setShowModal(true);
    setDraftName('');
  };

  const closeModal = () => {
    setShowModal(false);
    setDraftName('');
  };

  const handleSelectChange = (event) => {
    const selected = event.target.value;
    if (selected === '__add__') {
      openModal();
      event.currentTarget.value = value;
      return;
    }
    onChange(selected);
  };

  const handleSave = () => {
    const trimmed = draftName.trim();
    if (!trimmed) return;
    addSalesperson(trimmed);
    onChange(trimmed);
    closeModal();
  };

  return (
    <>
      <SearchableSelect
        className={className || "sel w-100"}
        value={value}
        onFocus={() => { void loadSalespersons(); }}
        onChange={handleSelectChange}
      >
        <option value="" disabled hidden>Select Salesperson</option>
        {hasCurrentValue && <option value={value}>{value}</option>}
        {loading && <option disabled>Loading salespersons...</option>}
        {error && <option disabled>Failed to load salespersons</option>}
        {showEmpty && <option disabled>No salespersons found</option>}
        {salespersons.map((name) => (
          <option key={name} value={name}>{name}</option>
        ))}
        <option value="__add__" style={{ fontWeight: 700 }}>Add new...</option>
      </SearchableSelect>
      {status && <div className="input-subtext mt-2">{status}</div>}

      {showModal && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.15)', // Very light overlay instead of heavy dark blur
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
        >
          <div
            className="animate-pop"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '380px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0,0,0,0.05)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                padding: '20px 24px 12px 24px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
                  Add Salesperson
                </h3>
                <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '4px 0 0 0' }}>
                  Save a name to reuse across sales.
                </p>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s',
                  marginTop: '-4px',
                  marginRight: '-8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
              >
                <i className="bi bi-x-lg" style={{ fontSize: '1.25rem' }}></i>
              </button>
            </div>
            
            <div style={{ padding: '8px 24px 20px 24px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: '#475569',
                  marginBottom: '8px',
                }}
              >
                Salesperson Name
              </label>
              <input
                type="text"
                placeholder="Enter name"
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSave();
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    closeModal();
                  }
                }}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '1rem',
                  outline: 'none',
                  color: '#1e293b',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              />
            </div>
            
            <div
              style={{
                padding: '16px 24px',
                background: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <button
                type="button"
                onClick={closeModal}
                style={{
                  padding: '10px 18px',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  borderRadius: '6px',
                  color: '#475569',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!draftName.trim()}
                style={{
                  padding: '10px 18px',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  borderRadius: '6px',
                  background: draftName.trim() ? '#3b82f6' : '#94a3b8',
                  color: '#fff',
                  border: 'none',
                  cursor: draftName.trim() ? 'pointer' : 'not-allowed',
                  boxShadow: draftName.trim() ? '0 2px 4px rgba(59, 130, 246, 0.2)' : 'none',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { if(draftName.trim()) e.currentTarget.style.background = '#2563eb' }}
                onMouseLeave={(e) => { if(draftName.trim()) e.currentTarget.style.background = '#3b82f6' }}
              >
                Add Salesperson
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalespersonField;

